/**
 * StepPostSession - Étape 6 : Post-séance, récupération, nutrition, résumé
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Star, Calendar, CalendarPlus, Droplets, Pill, Clock, Target, Flame, Trophy,
  ChevronDown, ChevronUp, Check, AlertCircle
} from 'lucide-react';
import { STRETCHES } from '../../data/exercises';
import { SUPPLEMENTS_POST, getPostFoodForLevel, getPostHydrationMl } from '../../data/nutrition';
import googleCalendarService from '../../../../services/googleCalendar';

export function StepPostSession({ session, level, stats, onRate, onClose, levelChange }) {
  const [rating, setRating] = useState(session?.rating || 0);
  const [showStretches, setShowStretches] = useState(true);
  const [showNutrition, setShowNutrition] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState('idle'); // idle | adding | added | error

  const duration = session?.duration || 0;
  const calories = session?.caloriesEstimated || Math.round((duration / 60) * (5 + level * 0.8));
  const postFood = getPostFoodForLevel(level);
  const postHydration = getPostHydrationMl(calories);

  const handleRate = (r) => {
    setRating(r);
    onRate?.(r);
  };

  const handleAddToCalendar = async () => {
    setCalendarStatus('adding');
    try {
      const nextSessionDate = new Date();
      nextSessionDate.setDate(nextSessionDate.getDate() + 2); // +48h
      nextSessionDate.setHours(10, 0, 0, 0);

      await googleCalendarService.createEvent({
        task: `Go Sport — Séance HIIT Niv.${level} #GoSport`,
        desc: `Séance HIIT prévue · Niveau ${level} · ~${session?.duration ? Math.round(session.duration / 60) : 30} min\n\n#GoSport #ADHDManager`,
        time: '10:00',
        duration: session?.duration ? Math.round(session.duration / 60) : 30,
        category: 'pleasure',
        instructions: [],
      }, nextSessionDate);

      setCalendarStatus('added');
    } catch {
      setCalendarStatus('error');
    }
  };

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m} min ${sec.toString().padStart(2, '0')}s`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4 space-y-5"
    >
      {/* Résumé héro */}
      <div className="text-center space-y-3">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.1 }}
          className="text-5xl"
        >
          🏆
        </motion.div>
        <h3 className="text-2xl font-black text-transparent bg-clip-text
          bg-gradient-to-r from-orange-400 to-yellow-400">
          SÉANCE TERMINÉE !
        </h3>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 flex items-center gap-3">
          <Clock className="w-5 h-5 text-blue-400 shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">{formatDuration(duration)}</p>
            <p className="text-[10px] text-zinc-500">Durée</p>
          </div>
        </div>
        <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 flex items-center gap-3">
          <Target className="w-5 h-5 text-orange-400 shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">Niv. {level}</p>
            <p className="text-[10px] text-zinc-500">Niveau</p>
          </div>
        </div>
        <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 flex items-center gap-3">
          <Flame className="w-5 h-5 text-red-400 shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">~{calories} kcal</p>
            <p className="text-[10px] text-zinc-500">Calories</p>
          </div>
        </div>
        <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 flex items-center gap-3">
          <Trophy className="w-5 h-5 text-yellow-400 shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">{stats?.streak?.current || 1}</p>
            <p className="text-[10px] text-zinc-500">Streak</p>
          </div>
        </div>
      </div>

      {/* Level change notification */}
      {levelChange?.leveledUp && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', delay: 0.3 }}
          className="bg-gradient-to-r from-green-500/20 to-emerald-500/20
            border border-green-500/40 rounded-xl p-3 text-center"
        >
          <p className="text-lg font-bold text-green-400">
            🎉 Niveau {levelChange.newLevel} débloqué !
          </p>
          <p className="text-xs text-green-300/70 mt-0.5">
            Ta régularité paie — les séances s'adaptent à ton nouveau niveau
          </p>
        </motion.div>
      )}
      {levelChange?.leveledDown && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', delay: 0.3 }}
          className="bg-zinc-800/60 border border-zinc-700/50 rounded-xl p-3 text-center"
        >
          <p className="text-sm font-medium text-zinc-300">
            Niveau ajusté à {levelChange.newLevel}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">
            Les prochaines séances seront plus adaptées à ton rythme
          </p>
        </motion.div>
      )}

      {/* Note */}
      <div className="text-center space-y-2">
        <p className="text-sm text-zinc-400">Comment était cette séance ?</p>
        <div className="flex justify-center gap-2">
          {[1, 2, 3, 4, 5].map(r => (
            <motion.button
              key={r}
              whileTap={{ scale: 0.8 }}
              onClick={() => handleRate(r)}
              className="transition-all"
            >
              <Star
                className={`w-8 h-8 ${r <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`}
              />
            </motion.button>
          ))}
        </div>
      </div>

      {/* Étirements */}
      <div className="border border-zinc-700/50 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowStretches(!showStretches)}
          className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800/40"
        >
          <span className="text-sm font-medium text-zinc-300">Étirements de récupération</span>
          {showStretches ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        {showStretches && (
          <div className="px-4 py-2 space-y-2">
            {STRETCHES.map((s, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-zinc-800 last:border-0">
                <div>
                  <p className="text-sm text-zinc-300">{s.name}</p>
                  <p className="text-[10px] text-zinc-500">{s.muscle}</p>
                </div>
                <span className="text-xs text-orange-400 font-medium">{s.duration}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Nutrition post */}
      <div className="border border-zinc-700/50 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowNutrition(!showNutrition)}
          className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800/40"
        >
          <span className="text-sm font-medium text-zinc-300">Nutrition post-séance</span>
          {showNutrition ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
        </button>
        {showNutrition && (
          <div className="px-4 py-3 space-y-3">
            {/* 30 min */}
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1.5">Dans les 30 min :</p>
              <div className="flex flex-wrap gap-1.5">
                {postFood.map((f, i) => (
                  <span key={i} className="text-xs bg-green-500/10 text-green-300 px-2 py-1 rounded-lg
                    border border-green-500/20">
                    {f}
                  </span>
                ))}
              </div>
            </div>

            {/* Hydratation */}
            <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg p-2">
              <Droplets className="w-4 h-4 text-blue-400" />
              <p className="text-xs text-blue-300">Boire {postHydration} ml d'eau</p>
            </div>

            {/* Compléments */}
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-1.5 flex items-center gap-1">
                <Pill className="w-3 h-3" /> Compléments recommandés
              </p>
              {SUPPLEMENTS_POST.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-1 text-xs">
                  <span className="text-zinc-300">{s.name}</span>
                  <span className="text-zinc-500">{s.timing} • {s.benefit}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Prochaine séance + Google Agenda */}
      <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 space-y-2.5">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-orange-400 shrink-0" />
          <div>
            <p className="text-sm font-medium text-orange-300">Prochaine séance recommandée</p>
            <p className="text-xs text-orange-400/70">Dans 48h pour une récupération optimale</p>
          </div>
        </div>

        {calendarStatus === 'added' ? (
          <div className="flex items-center gap-2 text-xs text-green-400">
            <Check className="w-4 h-4" />
            <span>Ajouté à Google Agenda</span>
          </div>
        ) : calendarStatus === 'error' ? (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle className="w-4 h-4" />
            <span>Erreur — vérifiez votre connexion Google</span>
          </div>
        ) : (
          <button
            onClick={handleAddToCalendar}
            disabled={calendarStatus === 'adding'}
            className="w-full py-2 rounded-lg bg-orange-500/20 hover:bg-orange-500/30
              border border-orange-500/30 text-orange-300 text-sm font-medium
              flex items-center justify-center gap-2 transition-colors
              disabled:opacity-50"
          >
            <CalendarPlus className="w-4 h-4" />
            {calendarStatus === 'adding' ? 'Ajout en cours…' : 'Ajouter à Google Agenda'}
          </button>
        )}
      </div>

      {/* Close */}
      <button
        onClick={onClose}
        className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500
          text-white font-bold flex items-center justify-center gap-2"
      >
        Terminer
      </button>
    </motion.div>
  );
}
